import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useGuruWidgetViewContext } from '../../hooks/useGuruWidgetViewContext';
import { portalService } from '../../services/portal';
import type { SentEmailItem, SentKindFilter, SentSenderFilter } from '../../types/portal';
import { formatTimeAgo, formatDateTime } from '../../utils/dateFormatters';
import { sanitizeEmailHtml } from '../../utils/sanitizeEmailHtml';
import { useIsolatedPrint } from '../../hooks/useIsolatedPrint';
import { buildPrintableMessages, formatSender, withoutQuotedHistory } from '../../utils/emailPrintModel';
import EmailPrintDocument, { type EmailPrintJob } from '../../components/portal/EmailPrintDocument';
import styles from './SentPage.module.css';

const PAGE_SIZE = 50;

const SENDER_TABS: { value: SentSenderFilter; label: string }[] = [
  { value: 'all', label: 'All Sent' },
  { value: 'me', label: 'Sent by Me' },
  { value: 'others', label: 'Sent by Others' },
];

// The orders@ mailbox also sends the sales site's automated mail (order
// notices, daily reports, alarms): ~90% of the folder on production. The
// desk opens on the mail a person wrote; Everything is one click away.
// The rule that tells the two apart lives in the backend
// (services/email/sent_folder_kind.py) and comes back as each row's `kind`.
const KIND_TABS: { value: SentKindFilter; label: string; hint: string }[] = [
  { value: 'person', label: 'Sent by a person', hint: 'Mail a CSR wrote: replies, forwards, confirmations sent from an order' },
  { value: 'everything', label: 'Everything', hint: "Also the sales site's automated notices, reports and alarms" },
];

/** Format the recipient for display: strip angle brackets, show name if present. */
function formatRecipient(to: string | null): string {
  if (!to) return '(no recipient)';
  // "Name <email>" → "Name" | "email" → "email"
  const match = to.match(/^(.+?)\s*<(.+?)>$/);
  if (match) return match[1].replace(/^"(.*)"$/, '$1');
  return to;
}

/** Render a small sender badge showing who sent the email. */
function SenderBadge({ email }: { email: SentEmailItem }) {
  if (email.sent_by_me === true) {
    return <span className={`${styles.senderBadge} ${styles.senderBadgeMe}`}>You</span>;
  }
  if (email.sender_name) {
    return (
      <span className={`${styles.senderBadge} ${styles.senderBadgeOther}`}>
        {email.sender_name}
      </span>
    );
  }
  // No matching sent_emails record — sender unknown (sent outside portal)
  return null;
}

/** Mark the sales site's automated mail when it is shown alongside a person's. */
function KindBadge({ email }: { email: SentEmailItem }) {
  if (email.kind !== 'system') return null;
  return (
    <span className={styles.kindBadge} title="Sent automatically by the sales site" data-testid="sent-kind-badge">
      System
    </span>
  );
}

const SentPage: React.FC = () => {
  const [emails, setEmails] = useState<SentEmailItem[]>([]);
  const [totalEmails, setTotalEmails] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [senderFilter, setSenderFilter] = useState<SentSenderFilter>('all');
  const [kindFilter, setKindFilter] = useState<SentKindFilter>('person');
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [detailEmail, setDetailEmail] = useState<SentEmailItem | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Tell the Guru feedback widget which sent email is in view (state-held,
  // not in the URL) so widget submissions carry the email identifiers.
  useGuruWidgetViewContext(
    'sent_email_detail',
    selectedEmailId
      ? {
          email_id: selectedEmailId,
          email_subject: detailEmail?.subject ?? undefined,
          email_from_address: detailEmail?.from_address ?? undefined,
          thread_id: detailEmail?.thread_id ?? undefined,
        }
      : null,
  );

  const loadedCountRef = useRef(0);

  // Fetch sent emails
  const fetchEmails = useCallback(
    async (loadMore = false) => {
      try {
        if (loadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setError(null);
        }
        const offset = loadMore ? loadedCountRef.current : 0;
        const response = await portalService.listSentEmails({
          limit: PAGE_SIZE,
          offset,
          search: debouncedSearch || undefined,
          sender: senderFilter !== 'all' ? senderFilter : undefined,
          kind: kindFilter,
        });
        if (loadMore) {
          setEmails((prev) => {
            const next = [...prev, ...response.emails];
            loadedCountRef.current = next.length;
            return next;
          });
        } else {
          setEmails(response.emails);
          loadedCountRef.current = response.emails.length;
        }
        setTotalEmails(response.total);
      } catch (err) {
        console.error('Failed to fetch sent emails:', err);
        if (!loadMore) {
          setError('Failed to load sent emails. Please try again.');
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, senderFilter, kindFilter],
  );

  useEffect(() => {
    fetchEmails(false);
  }, [fetchEmails]);

  // Clear selection when search or a filter changes
  useEffect(() => {
    setSelectedEmailId(null);
    setDetailEmail(null);
  }, [debouncedSearch, senderFilter, kindFilter]);

  const handleLoadMore = useCallback(() => {
    fetchEmails(true);
  }, [fetchEmails]);

  // Open detail modal
  const handleOpenDetail = async (emailId: string) => {
    setSelectedEmailId(emailId);
    setLoadingDetail(true);
    try {
      const detail = await portalService.getSentEmailDetail(emailId);
      setDetailEmail(detail);
    } catch (err) {
      console.error('Failed to load sent email detail:', err);
      setDetailEmail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedEmailId(null);
    setDetailEmail(null);
  };

  // Print: the same isolated document the inbox uses, so only the email
  // (headers + body) reaches paper, never this page or the modal. A sent
  // reply prints what was written, not the conversation quoted under it
  // (owner ruling on review c310d3f1).
  const { job: printJob, startPrint, handleReady: handlePrintReady } = useIsolatedPrint<EmailPrintJob>();
  const handlePrint = () => {
    if (!detailEmail) return;
    startPrint({
      messages: buildPrintableMessages([], {
        id: detailEmail.id,
        subject: detailEmail.subject,
        from: formatSender(detailEmail.sender_name, detailEmail.from_address),
        to: detailEmail.to_address,
        cc: detailEmail.cc_addresses,
        receivedAt: detailEmail.sent_at,
        bodyText: detailEmail.body_text,
        bodyHtml: detailEmail.body_html,
        attachments: detailEmail.attachments ?? [],
      }).map(withoutQuotedHistory),
    });
  };

  if (error) {
    return (
      <div className={styles.header}>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#e74c3c' }}>
          <p>{error}</p>
          <button
            className={`${styles.btn} ${styles.btnOutline}`}
            onClick={() => fetchEmails(false)}
            type="button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div>
            <h1 className={styles.title}>Sent</h1>
            <span className={styles.subtitle} data-testid="sent-subtitle">
              {kindFilter === 'person' ? 'Mail written by a person' : 'Everything the mailbox sent'} &middot;{' '}
              {totalEmails.toLocaleString()} total
            </span>
          </div>
        </div>
        <div className={styles.actions}>
          <button
            className={`${styles.btn} ${styles.btnOutline}`}
            onClick={() => fetchEmails(false)}
            type="button"
            data-action="sent_refresh"
            data-outcome="request"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Kind (person / everything) and sender filter tabs */}
      <div className={styles.filterRow} data-section="sent_filters">
        <div className={styles.filterTabs} role="group" aria-label="What to show">
          {KIND_TABS.map((tab) => (
            <button
              key={tab.value}
              className={`${styles.filterTab} ${kindFilter === tab.value ? styles.filterTabActive : ''}`}
              onClick={() => setKindFilter(tab.value)}
              type="button"
              value={tab.value}
              title={tab.hint}
              aria-pressed={kindFilter === tab.value}
              data-action="sent_kind_filter"
              data-record-value="kind"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.filterTabs} role="group" aria-label="Who sent it">
          {SENDER_TABS.map((tab) => (
            <button
              key={tab.value}
              className={`${styles.filterTab} ${senderFilter === tab.value ? styles.filterTabActive : ''}`}
              onClick={() => setSenderFilter(tab.value)}
              type="button"
              value={tab.value}
              aria-pressed={senderFilter === tab.value}
              data-action="sent_sender_filter"
              data-record-value="sender"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search bar */}
      <div className={styles.searchBar} data-section="sent_search">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          className={styles.searchInput}
          type="text"
          role="searchbox"
          aria-label="Search sent emails"
          placeholder="Search by subject, recipient, or content..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-action="sent_search"
          data-record-value="query"
        />
      </div>

      {/* Email list */}
      <div className={styles.list} data-section="sent_list">
        {loading ? (
          <div className={styles.emptyState}>
            <p>Loading sent emails...</p>
          </div>
        ) : emails.length === 0 ? (
          <div className={styles.emptyState}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
            <p>
              {debouncedSearch
                ? 'No sent emails match your search'
                : senderFilter === 'me'
                  ? 'No emails sent by you'
                  : senderFilter === 'others'
                    ? 'No emails sent by others'
                    : kindFilter === 'person'
                      ? 'No emails sent by a person yet'
                      : 'No sent emails found'}
            </p>
            {kindFilter === 'person' && (
              <button
                className={`${styles.btn} ${styles.btnOutline}`}
                onClick={() => setKindFilter('everything')}
                type="button"
                value="everything"
                data-action="sent_kind_filter"
                data-record-value="kind"
              >
                Show everything the mailbox sent
              </button>
            )}
          </div>
        ) : (
          <>
            {emails.map((email) => (
              <div
                key={email.id}
                className={`${styles.emailRow} ${selectedEmailId === email.id ? styles.emailRowSelected : ''}`}
                onClick={() => handleOpenDetail(email.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleOpenDetail(email.id);
                }}
                data-action="sent_email_open"
                data-outcome="open"
                data-target-email-id={email.id}
                data-kind={email.kind}
              >
                {/* Send icon */}
                <svg className={styles.sentIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>

                {/* Email content */}
                <div className={styles.emailMain}>
                  <div className={styles.emailTo}>
                    <span className={styles.toLabel}>To:</span>
                    {formatRecipient(email.to_address)}
                    {' '}
                    <SenderBadge email={email} />
                    <KindBadge email={email} />
                  </div>
                  <div className={styles.emailSubject}>{email.subject || '(no subject)'}</div>
                  <div className={styles.emailPreview}>{email.preview}</div>
                </div>

                {/* Meta info */}
                <div className={styles.emailMeta}>
                  <span className={styles.emailTime}>
                    {email.sent_at ? formatTimeAgo(email.sent_at) : ''}
                  </span>
                  {email.attachments.length > 0 && (
                    <span className={styles.attachmentBadge}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                      {email.attachments.length}
                    </span>
                  )}
                </div>
              </div>
            ))}

            {/* Pagination */}
            <div className={styles.paginationFooter}>
              <span className={styles.paginationCount}>
                Showing {emails.length} of {totalEmails.toLocaleString()}
              </span>
              {emails.length < totalEmails && (
                <button
                  className={`${styles.btn} ${styles.btnOutline} ${styles.loadMoreBtn}`}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  type="button"
                  data-action="sent_load_more"
                  data-outcome="request"
                >
                  {loadingMore
                    ? 'Loading...'
                    : `Load More (${Math.min(PAGE_SIZE, totalEmails - emails.length)} more)`}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Sent email detail modal */}
      {selectedEmailId && (
        <div
          className={styles.modalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCloseDetail();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div className={styles.modalSubject}>
                {loadingDetail ? 'Loading...' : detailEmail?.subject || '(no subject)'}
              </div>
              <div className={styles.modalHeaderActions}>
                <button
                  className={styles.modalClose}
                  onClick={handlePrint}
                  type="button"
                  aria-label="Print"
                  title="Print email"
                  disabled={!detailEmail || loadingDetail}
                  data-testid="sent-print"
                  data-action="sent_print"
                  data-target-email-id={detailEmail?.id}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                  </svg>
                </button>
                <button
                  className={styles.modalClose}
                  onClick={handleCloseDetail}
                  type="button"
                  aria-label="Close"
                  data-action="sent_close"
                  data-target-email-id={selectedEmailId}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {detailEmail && !loadingDetail && (
              <>
                <div className={styles.modalMeta}>
                  <div>
                    <span className={styles.modalMetaLabel}>From:</span>
                    <span className={styles.modalMetaValue}>{detailEmail.from_address || 'Unknown'}</span>
                    {detailEmail.sender_name && (
                      <>
                        {' '}
                        <span className={`${styles.senderBadge} ${detailEmail.sent_by_me ? styles.senderBadgeMe : styles.senderBadgeOther}`}>
                          {detailEmail.sent_by_me ? 'You' : detailEmail.sender_name}
                        </span>
                      </>
                    )}
                  </div>
                  <div>
                    <span className={styles.modalMetaLabel}>To:</span>
                    <span className={styles.modalMetaValue}>{detailEmail.to_address || 'Unknown'}</span>
                  </div>
                  {detailEmail.cc_addresses && (
                    <div>
                      <span className={styles.modalMetaLabel}>CC:</span>
                      <span className={styles.modalMetaValue}>{detailEmail.cc_addresses}</span>
                    </div>
                  )}
                  <div>
                    <span className={styles.modalMetaLabel}>Date:</span>
                    <span className={styles.modalMetaValue}>
                      {detailEmail.sent_at
                        ? formatDateTime(detailEmail.sent_at)
                        : 'Unknown'}
                    </span>
                  </div>
                </div>
                <div className={styles.modalBody}>
                  {detailEmail.body_html ? (
                    <iframe
                      className={styles.modalHtmlFrame}
                      srcDoc={`<head><base target="_blank" rel="noopener noreferrer"></head>${sanitizeEmailHtml(detailEmail.body_html)}`}
                      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                      title="Sent email body"
                    />
                  ) : (
                    <div className={styles.modalBodyText}>
                      {detailEmail.body_text || '(no content)'}
                    </div>
                  )}
                </div>
              </>
            )}

            {loadingDetail && (
              <div className={styles.emptyState}>
                <p>Loading email content...</p>
              </div>
            )}
          </div>
        </div>
      )}
      {printJob && <EmailPrintDocument job={printJob} onReady={handlePrintReady} />}
    </>
  );
};

export default SentPage;
